import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import apiRequest from "../../../utils/apiRequest";
import {
  getAttributeValue,
  getQualityReportItems,
} from "../../../utils/productDetailHelper";
import { formatToINR, getMainImageUrl } from "../../../utils/helper";
import Loader from "../../ui/loader/Loader";

const ProductDetails = ({ order, closeModal }) => {
  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedProductId, setExpandedProductId] = useState(null);

  const getProductDetail = async () => {
    setIsLoading(true);
    try {
      const res = await apiRequest.getOrderedProductWithAttributeDetail({
        id: order?._id,
      });
      const data = res.data?.data;
      setOrderData(data);
      if (data?.products?.length > 0) {
        setExpandedProductId(data.products[0]._id);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Something went wrong"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProductDetail();
  }, []);

  if(isLoading){
    return <div className="flex h-[200px] items-center justify-center"><Loader /></div>
  }

  if (!orderData) {
    return (
      <div className="p-6 text-center text-gray-600">
        Unable to load product details. Please try again.
      </div>
    );
  }

  const selectedProduct = orderData.products.find(
    (p) => p._id === expandedProductId
  );
  const productImage = getMainImageUrl(selectedProduct?.images);
  const experience = getAttributeValue(selectedProduct, "Experience") || "";
  const variant = getAttributeValue(selectedProduct, "Variant") || "";
  const qualityReportItems = getQualityReportItems(selectedProduct);
  const descriptionAttr = selectedProduct?.attributes?.find(
    (item) => item.key?.name === "Description"
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Order #{order.orderId}
        </h2>
      </div>

      {/* Order Info */}
      <div className="p-4 mb-6 text-sm text-gray-700 border border-gray-200 rounded-lg bg-gray-50">
        <p>
          <strong>Customer:</strong> {order.userId?.name} ({order.userId?.email}
          )
        </p>
        <p>
          <strong>Address:</strong> {order.address?.addressLine1},{" "}
          {order.address?.addressLine2}, {order.address?.city},{" "}
          {order.address?.state}, {order.address?.pincode},{" "}
          {order.address?.country}
        </p>
        <p>
          <strong>Total:</strong> {formatToINR(order.totalAmount)}
        </p>
        <p>
          <strong>Ordered At:</strong>{" "}
          {new Date(order.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
          })}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {orderData.products.map((product) => (
          <button
            key={product._id}
            onClick={() => setExpandedProductId(product._id)}
            className={`px-4 py-2 text-sm rounded-md border ${
              product._id === expandedProductId
                ? "bg-emerald-500 text-white"
                : "bg-white border-gray-300 text-gray-800 hover:bg-gray-100"
            }`}
          >
            {product.modelId?.name}
          </button>
        ))}
      </div>

      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <div className="mb-2 text-xl font-semibold text-gray-800">
          {selectedProduct?.modelId?.name}
          {selectedProduct?.category?.titleOption !== "Not Applicable" && (
            <span className="ml-2 text-sm font-medium text-gray-700">
              {selectedProduct.title}
            </span>
          )}
          {variant && ` (${variant})`}
        </div>

        {experience && (
          <div className="mb-3 text-sm font-semibold text-orange-600">
            {experience}
          </div>
        )}

        {!selectedProduct?.category?.hasStock && (
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 mr-3 font-bold text-black rounded-lg bg-gradient-to-r from-green-100 to-emerald-100">
              35
            </div>
            <h3 className="font-bold text-gray-800 text-md">
              Points Quality Check Report
            </h3>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 md:grid-cols-3">
          {qualityReportItems
            ?.filter((item) => item.value !== undefined)
            .map((item, index) => (
              <div
                key={index}
                className={`bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-start gap-2 ${
                  item.fullWidth || Array.isArray(item.value)
                    ? "sm:col-span-2 md:col-span-3"
                    : ""
                }`}
              >
                {!Array.isArray(item.value) && (
                  <div className="text-sm text-gray-600">{item.icon}</div>
                )}
                <div>
                  <p className="text-xs text-gray-500">{item.label}</p>
                  {Array.isArray(item.value) ? (
                    <ul className="text-xs font-medium text-gray-800 list-disc list-inside">
                      {item.value.map((val, index) => (
                        <li key={index}>{val}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="font-medium text-gray-800">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
        </div>

        <div className="p-5 mt-5 bg-white border border-gray-200 rounded-lg">
          <div className="mb-4">
            <h3 className="flex items-center text-lg font-semibold text-gray-800">
              <svg
                className="w-5 h-5 mr-2 text-blue-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              Additional Information
            </h3>
          </div>

          {descriptionAttr?.value?.value && (
            <p className="mb-4 text-gray-600">{descriptionAttr.value.value}</p>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {selectedProduct.attributes?.map(
              (item, index) =>
                item.key?.name !== "Description" && (
                  <div
                    key={`attr-${index}`}
                    className="p-3 rounded-md bg-gray-50"
                  >
                    <p className="text-xs text-gray-500">{item.key?.name}</p>
                    <p className="text-xs font-medium">{item.value?.value}</p>
                  </div>
                )
            )}

            {selectedProduct.attributeQuestion?.map((item, index) => (
              <div
                key={`question-${index}`}
                className="p-3 rounded-md bg-gray-50"
              >
                <p className="text-xs text-gray-500">
                  {item.questionId?.question}
                </p>
                {Array.isArray(item.answer) ? (
                  <ul className="pl-5 list-disc">
                    {item.answer.map((ans, i) => (
                      <li key={i} className="text-xs font-medium">
                        {ans}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs font-medium">{item.answer}</p>
                )}
              </div>
            ))}

            {selectedProduct.informationAttributes?.map((item, index) => (
              <div key={`info-${index}`} className="p-3 rounded-md bg-gray-50">
                <p className="text-sm text-gray-500">{item.key}</p>
                <p className="font-medium">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
